import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  useWorkspace,
  useWorkspaceId,
  useWorkspaceInitializing,
} from '../../contexts/WorkspaceContext';
import { clearInvitedWorkspace, readInvitedWorkspace } from '../../utils/pendingInvitation';
import { isWorkspaceSwitchSupersededError } from '../../utils/workspaceSwitch';

/**
 * Opens the workspace an accepted invitation joined (`recordInvitedWorkspace`).
 *
 * Runs after the workspace boot rather than inside the accept flow, because
 * every accept path can lose its own switch: Google/Microsoft sign-in accepts
 * on the server and boots into the last-used workspace, and AuthRedirect's
 * switch is cancelled when an auth re-render restarts its effect. Waiting for
 * `initializing` to settle also means the boot cannot overwrite the switch.
 *
 * A not-yet-onboarded invitee is skipped: onboarding activates the invited
 * workspace itself, and the marker is cleared as a no-op once it has.
 */
export function usePendingInvitedWorkspaceSwitch(): void {
  const { user } = useAuth();
  const { switchWorkspace } = useWorkspace();
  const workspaceId = useWorkspaceId();
  const initializing = useWorkspaceInitializing();
  // uid, not `internalId || uid`: that expression churns for one person when a
  // best-effort enrichment fails, and a mismatch deletes the marker.
  const identity = user?.uid ?? null;
  const onboarded = user?.onboarded === true;

  useEffect(() => {
    if (!identity || !onboarded || initializing) return;
    const target = readInvitedWorkspace(identity);
    if (!target) return;
    if (target === workspaceId) {
      clearInvitedWorkspace();
      return;
    }
    switchWorkspace(target)
      .then(() => clearInvitedWorkspace())
      .catch((error: unknown) => {
        // Superseded is benign: another switch won, and the next pass of this
        // effect either finds the user in the target or tries again.
        if (isWorkspaceSwitchSupersededError(error)) return;
        console.warn('Could not open the workspace from the accepted invitation', error);
        clearInvitedWorkspace();
      });
  }, [identity, onboarded, initializing, workspaceId, switchWorkspace]);
}
